// @avantia common-code
import { ensureDefined, SMap } from '@avantia/client-and-server-utilities';
import { HpValidationResult } from '@avantia/form-validation';
import { sectionPrerequisitesHaveBeenMet } from '../tools/questionPrerequisites';
import { HpClientQuestionSetState } from '../types';
import { moveSectionWithoutChecks } from './questionSetReducerLibrary';
import { iterateSections, validateSection } from './questionSetTools';
import { swapInQuestions } from './reducerOptimiser';

export interface HpGetValidationForEachSectionProps {
  raiseNavigationChangedEvent?: boolean;
  state: HpClientQuestionSetState;
}

export interface HpGetValidationForEachSectionResult {
  firstSectionId: string;
  firstSectionIdWithErrors: string | undefined;
  firstSectionPosition: number;
  highestSectionIdStarted: string | undefined;
  highestSectionPositionStarted: number | undefined;
  highestValidSectionId: string | undefined;
  highestValidSectionPosition: number | undefined;
  sectionIdsValidated: string[];
  sectionValidationMap: SMap<HpValidationResult>;
  validSections: string[];
}

export function getValidationForEachSection({
  state: partialState,
  raiseNavigationChangedEvent = true
}: HpGetValidationForEachSectionProps): HpGetValidationForEachSectionResult {
  const state = swapInQuestions(partialState);

  const { displayState } = state;
  const { sectionIdOrder, sections, sectionsUI } = displayState;
  const sectionValidationMap: SMap<HpValidationResult> = {};
  const sectionIdsValidated: string[] = [];
  const validSections: string[] = [];
  let highestSectionIdStarted: string | undefined;
  let firstSectionIdWithErrors: string | undefined;
  let highestValidSectionId: string | undefined;
  let firstSectionId: string | undefined;
  let firstVisible = true;
  let firstWithErrors = false;
  let firstState: HpClientQuestionSetState;

  iterateSections({
    sectionIdOrder,
    sections,
    sectionsUI,
    action: ({ id, ui }) => {
      if (!ui.isVisible) {
        return;
      }

      let currState: HpClientQuestionSetState;
      if (firstVisible) {
        firstVisible = false;
        firstSectionId = id;
        firstState = moveSectionWithoutChecks({
          state,
          moveToSectionId: id,
          raiseNavigationChangedEvent
        });
        currState = firstState;
      } else {
        currState = moveSectionWithoutChecks({
          state: firstState,
          moveToSectionId: id,
          raiseNavigationChangedEvent
        });
      }

      const section = currState.displayState.sections[id];
      if (section.prerequisites && section.prerequisites.length > 0) {
        if (!sectionPrerequisitesHaveBeenMet(id, 'none', currState)) {
          return;
        }
      }

      const validation = validateSection({ state: currState, sectionId: id });
      sectionValidationMap[id] = validation;
      sectionIdsValidated.push(id);
      if (validation.count === 0) {
        highestValidSectionId = id;
        validSections.push(id);
      } else if (!firstWithErrors) {
        firstWithErrors = true;
        highestSectionIdStarted = id; // TODO Make this better - we should check for a completed answer perhaps?
        firstSectionIdWithErrors = id;
      }
    }
  });

  if (!highestSectionIdStarted) {
    highestSectionIdStarted = firstSectionId;
  }

  validSections.sort();

  return {
    sectionValidationMap,
    sectionIdsValidated,
    validSections,
    highestSectionIdStarted: highestSectionIdStarted,
    highestSectionPositionStarted: highestSectionIdStarted ? sections[highestSectionIdStarted].position : undefined,
    highestValidSectionId,
    highestValidSectionPosition: highestValidSectionId ? sections[highestValidSectionId].position : undefined,
    firstSectionId: ensureDefined(firstSectionId, 'firstSectionId'),
    firstSectionPosition: sections[firstSectionId || ''].position,
    firstSectionIdWithErrors
  };
}
